import React from "react";
import { Stat } from "./Stat";

/**
 * Live progress rendering for COMMITTING / CANCELLING jobs.
 *
 * Purely presentational — polling, request orchestration, and canonical job
 * state remain in the owning `Step5Progress` step. The parent computes the
 * numbers from `useImportProgress` + `job.counts` and hands them in.
 */
export function ProgressPanel({
  percent,
  processed,
  total,
  remaining,
  created,
  updated,
  skipped,
  failed,
  heartbeatAt,
  inProgress,
}: {
  percent: number | null;
  processed: number;
  total: number;
  remaining: number | null;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  heartbeatAt: string | null;
  inProgress: boolean;
}) {
  const heartbeatStale =
    heartbeatAt && Date.now() - new Date(heartbeatAt).getTime() > 30_000;

  return (
    <div className="space-y-4" data-testid="progress-panel">
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-sm text-slate-600" data-testid="progress-processed">
            {processed} of {total || "…"} rows
          </span>
          <span className="text-xs text-slate-500" data-testid="progress-percent">
            {percent === null ? "—" : `${percent}%`}
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total || 100}
          aria-valuenow={processed}
          aria-label="Import progress"
          className="h-2 rounded-full bg-slate-100 overflow-hidden"
          data-testid="progress-bar"
        >
          <div
            className="h-full bg-slate-900 transition-[width] motion-reduce:transition-none"
            style={{ width: `${percent ?? 0}%` }}
          />
        </div>
        {remaining !== null && (
          <div className="mt-1 text-[11px] text-slate-500" data-testid="progress-remaining">
            {remaining} rows remaining
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" aria-live="polite">
        <Stat label="Created" value={created} tone="ok" testId="stat-created" />
        <Stat label="Updated" value={updated} tone="ok" testId="stat-updated" />
        <Stat label="Skipped" value={skipped} tone="muted" testId="stat-skipped" />
        <Stat
          label="Failed"
          value={failed}
          tone={failed ? "bad" : "muted"}
          testId="stat-failed"
        />
      </div>

      <div className="text-[11px] text-slate-500" data-testid="progress-worker-line">
        {inProgress ? (
          <>
            Worker active. Last heartbeat:{" "}
            <span className={heartbeatStale ? "text-amber-800" : ""}>
              {heartbeatAt ? new Date(heartbeatAt).toLocaleTimeString() : "—"}
            </span>
            {heartbeatStale && (
              <span className="ml-1 text-amber-800" data-testid="heartbeat-stale">
                (awaiting next update)
              </span>
            )}
          </>
        ) : (
          "Worker not yet claimed this job — it will pick it up shortly."
        )}
      </div>
    </div>
  );
}
